import { Component, input, ChangeDetectionStrategy, signal, computed, inject, LOCALE_ID } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ChartModule } from '@progress/kendo-angular-charts';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { SVGIcon } from '@progress/kendo-angular-icons';
import { moreVerticalIcon } from '@progress/kendo-svg-icons';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { formatDateRangeLabel } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';
import { DeviceTypeChartResponse } from '../../../../../../../core/models/analytics/vss-models';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-device-type-chart',
  templateUrl: './device-type-chart.component.html',
  styleUrls: ['./device-type-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ChartModule, ButtonsModule, DialogsModule, DatatableComponent],
})
export class DeviceTypeChartComponent {
  private locale = inject(LOCALE_ID);

  deviceTypeChartResponse = input.required<DeviceTypeChartResponse>();

  public organizationId = input<number | null>();
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Vulnerabilities by Device Type (${label})` : 'Vulnerabilities by Device Type';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null
      ? `${environment.apiUrl}/v1/vicarius/analytics/vulnerabilitycountbydevicetypedetails(${id})`
      : '';
  });

  /** The same period the chart was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  public datatableColumns = [
    { data: 'device_type', name: 'device_type', title: 'Device Type', type: 'string', render: text },
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'vulnerability_count', name: 'vulnerability_count', title: 'Vulnerabilities', type: 'num' },
    {
      data: 'last_detected_at',
      name: 'last_detected_at',
      title: 'Last Detected',
      type: 'string',
      render: this.date,
    },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
